/*
 * <about-carousel> — native scroll-snap track with optional page dots and prev/next buttons.
 * Markup: [data-track] scroller, optional [data-dots] container, optional [data-prev]/[data-next] buttons.
 * Dots are one per "page" (a viewport-width of scroll), so the count adapts to screen size.
 */
if (!customElements.get("about-carousel")) {
  class AboutCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-track]");
      if (!this.track) return;

      this.dots = this.querySelector("[data-dots]");
      this.prev = this.querySelector("[data-prev]");
      this.next = this.querySelector("[data-next]");

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.track.addEventListener("scroll", this.onScroll, { passive: true });
      this.prev?.addEventListener("click", () => this.step(-1));
      this.next?.addEventListener("click", () => this.step(1));

      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this.track?.removeEventListener("scroll", this.onScroll);
      this.resizeObserver?.disconnect();
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    get pageCount() {
      if (this.maxScroll <= 1) return 1;
      return Math.ceil(this.maxScroll / this.track.clientWidth) + 1;
    }

    get currentPage() {
      const max = this.maxScroll;
      if (max <= 1) return 0;
      if (Math.abs(this.track.scrollLeft) >= max - 2) return this.pageCount - 1;
      return Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
    }

    build() {
      if (this.dots) {
        const count = this.pageCount;
        if (this.dots.childElementCount !== count) {
          this.dots.replaceChildren(
            ...Array.from({ length: count }, (_, i) => {
              const dot = document.createElement("button");
              dot.type = "button";
              dot.className = "about-carousel__dot";
              dot.setAttribute("aria-label", `${this.dataset.dotLabel || "Page"} ${i + 1}`);
              dot.addEventListener("click", () => this.goTo(i));
              return dot;
            })
          );
        }
        this.dots.hidden = count < 2;
      }
      this.update();
    }

    update() {
      const page = this.currentPage;
      this.dots?.querySelectorAll("button").forEach((dot, i) => {
        dot.setAttribute("aria-current", i === page ? "true" : "false");
      });
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.track.scrollLeft >= this.maxScroll - 1;
    }

    goTo(page) {
      const left = Math.min(page * this.track.clientWidth, this.maxScroll);
      this.track.scrollTo({ left, behavior: this.behavior });
    }

    step(direction) {
      const slide = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
      this.track.scrollBy({ left: direction * distance, behavior: this.behavior });
    }

    get behavior() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    }
  }

  customElements.define("about-carousel", AboutCarousel);
}
